import _cloneDeep from "lodash/cloneDeep";
import { LoggingModule } from "@pns-fe/sdk-common-modules";
import CryptoJS from "crypto-js";
import { CONSENT_DATA_STORAGE_NAME, NOTIFICATION_STORAGE_NAME } from "../../../constants/env";
import { StorageName, StorageType } from "../../../constants/sdk";
import BaseStorageWrapper, { DATA_TEMPLATE } from "./BaseStorageWrapper";
class WebStorageWrapper extends BaseStorageWrapper {
  constructor(name = "WebStorageWrapper", storageName = StorageType.sessionStorage) {
    super(name);
    this._decrypt = (encryptedStr, deviceId, userId) => {
      LoggingModule.info(`[${this.name}]: _decrypt`);
      let data = null;
      if (deviceId || userId) {
        const key = `${deviceId != null ? deviceId : ""}_${userId != null ? userId : ""}`;
        try {
          const decryptedBytes = CryptoJS.AES.decrypt(encryptedStr, key);
          const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
          const parsedData = JSON.parse(decryptedString);
          if ((parsedData == null ? void 0 : parsedData.data) && typeof (parsedData == null ? void 0 : parsedData.timestamp) === "number") {
            data = parsedData;
          }
        } catch (error) {
          LoggingModule.info(`[${this.name}]: _decrypt - ${error.message}`, error);
        }
      }
      LoggingModule.info(`[${this.name}]: _decrypt - finish`, data);
      return data;
    };
    this._encrypt = (data, userInfo) => {
      LoggingModule.info(`[${this.name}]: _encrypt`);
      const { device_id: deviceId = "", user_id: userId = "" } = userInfo != null ? userInfo : {};
      let str = "";
      if (deviceId || userId) {
        const key = `${deviceId != null ? deviceId : ""}_${userId != null ? userId : ""}`;
        str = CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
      }
      return str;
    };
    this._encryptConsentStorage = (store, userInfo) => {
      return {
        [StorageName.ENTITIES]: this._encrypt(store[StorageName.ENTITIES], userInfo),
        [StorageName.COLLECTION_POINTS]: JSON.stringify(store[StorageName.COLLECTION_POINTS])
      };
    };
    this._setConsentWebStorage = (consentStore, userInfo) => {
      const storageDataWithEncryption = this._encryptConsentStorage(consentStore, userInfo);
      window[this._storageName].setItem(CONSENT_DATA_STORAGE_NAME, JSON.stringify(storageDataWithEncryption));
    };
    this.insertConsentData = (data, timestamp, userInfo) => {
      LoggingModule.info(`[${this.name}]: insertConsentData`, {
        data,
        timestamp,
        userInfo
      });
      try {
        const store = this.insertConsentDataIntoStore(data, timestamp, userInfo);
        this._setConsentWebStorage(store, userInfo);
      } catch (error) {
        LoggingModule.error(`[${this.name}]: insertConsentData - ${error.message}`, error);
        throw error;
      }
    };
    this.insertNotificationData = (data, timestamp, userInfo) => {
      LoggingModule.info(`[${this.name}]: insertNotificationData`, {
        data,
        timestamp,
        userInfo
      });
      try {
        const store = this.getNotificationStore(userInfo);
        this.insertDataByStorageName(store, StorageName.NOTIFICATIONS, data, timestamp);
        window[this._storageName].setItem(NOTIFICATION_STORAGE_NAME, JSON.stringify(store));
      } catch (error) {
        LoggingModule.error(`[${this.name}]: insertNotificationData - ${error.message}`, error);
        throw error;
      }
    };
    this.getConsentStore = (userInfo) => {
      var _a, _b;
      LoggingModule.info(`[${this.name}]: getConsentStore`, {
        userInfo
      });
      this.cleanUpUnrelatedUserData(userInfo);
      const { device_id: deviceId = "", user_id: userId = "" } = userInfo != null ? userInfo : {};
      let entitiesData = _cloneDeep(DATA_TEMPLATE);
      let collectionPointData = _cloneDeep(DATA_TEMPLATE);
      try {
        const currentStr = (_a = window[this._storageName].getItem(CONSENT_DATA_STORAGE_NAME)) != null ? _a : "{}";
        const storeWithEncryptionData = (_b = JSON.parse(currentStr)) != null ? _b : {};
        entitiesData = this._retrieveEntitiesData(storeWithEncryptionData, deviceId, userId);
        collectionPointData = this._retrieveCollectionPointData(storeWithEncryptionData);
      } catch (error) {
        LoggingModule.error(`[${this.name}]: getConsentStore - ${error.message}`, error);
      }
      return {
        [StorageName.ENTITIES]: entitiesData,
        [StorageName.COLLECTION_POINTS]: collectionPointData
      };
    };
    this.getNotificationStore = (userInfo) => {
      var _a, _b;
      LoggingModule.info(`[${this.name}]: getNotificationStore`, {
        userInfo
      });
      let notification = _cloneDeep(DATA_TEMPLATE);
      try {
        const currentStr = (_a = window[this._storageName].getItem(NOTIFICATION_STORAGE_NAME)) != null ? _a : "{}";
        notification = (_b = JSON.parse(currentStr)) != null ? _b : {};
      } catch (error) {
        LoggingModule.error(`[${this.name}]: getNotificationStore - ${error.message}`, error);
      }
      return {
        [StorageName.NOTIFICATIONS]: notification
      };
    };
    this.cleanConsentData = () => {
      LoggingModule.info(`[${this.name}]: cleanConsentData`);
      window[this._storageName].removeItem(CONSENT_DATA_STORAGE_NAME);
    };
    this.cleanNotificationData = () => {
      LoggingModule.info(`[${this.name}]: cleanNotificationData`);
      window[this._storageName].removeItem(NOTIFICATION_STORAGE_NAME);
    };
    this.clean = () => {
      window[this._storageName].removeItem(CONSENT_DATA_STORAGE_NAME);
      window[this._storageName].removeItem(NOTIFICATION_STORAGE_NAME);
    };
    this.destruct = () => {
      this.clean();
    };
    this._storageName = storageName;
  }
  _retrieveEntitiesData(storeWithEncryptionData, deviceId, userId) {
    const defaultData = _cloneDeep(DATA_TEMPLATE);
    const data = this._decrypt(storeWithEncryptionData[StorageName.ENTITIES], deviceId, userId);
    if (data) {
      return data;
    }
    return defaultData;
  }
  _retrieveCollectionPointData(storeWithEncryptionData) {
    const defaultData = _cloneDeep(DATA_TEMPLATE);
    let data = null;
    try {
      data = JSON.parse(storeWithEncryptionData[StorageName.COLLECTION_POINTS]);
    } catch (error) {
      LoggingModule.info(`[${this.name}]: _retrieveCollectionPointData - ${error.message}`, error);
    }
    if (data) {
      return data;
    }
    return defaultData;
  }
}
class LocalStorageWrapper extends WebStorageWrapper {
  constructor() {
    super("LocalStorageWrapper", StorageType.localStorage);
  }
}
class SessionStorageWrapper extends WebStorageWrapper {
  constructor() {
    super("SessionStorageWrapper", StorageType.sessionStorage);
  }
}
export {
  LocalStorageWrapper,
  SessionStorageWrapper
};
